import type { AnalyticsView } from "@shared/schema";
import { useQuery } from "@tanstack/react-query";
import { Settings, Trash2 } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { orpc } from "@/lib/rpc";
import type { AnalyticsResponse } from "@/types/analytics";

const COLORS = [
  "#818cf8",
  "#34d399",
  "#f472b6",
  "#fb923c",
  "#a78bfa",
  "#22d3ee",
  "#fbbf24",
  "#4ade80",
  "#f87171",
  "#60a5fa",
];

interface JobAnalyticsTabProps {
  view: AnalyticsView;
  jobOpeningId: string;
  /** When provided, skip fetching and render this data directly (used for live preview). */
  previewData?: AnalyticsResponse;
  /** Called when the user wants to edit this view. */
  onEdit?: () => void;
  /** Called when the user wants to delete this view. */
  onDelete?: () => void;
}

export function JobAnalyticsTab({
  view,
  jobOpeningId,
  previewData,
  onEdit,
  onDelete,
}: JobAnalyticsTabProps) {
  const {
    data: fetchedData,
    isLoading,
    isError,
  } = useQuery({
    ...orpc.jobOpenings.getAnalyticsData.queryOptions({
      input: { id: jobOpeningId, viewId: view.id },
    }),
    enabled: !previewData,
  });

  const data = previewData ?? fetchedData;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
        Carregando dados...
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center justify-center h-64 text-destructive text-sm">
        Erro ao carregar dados do gráfico.
      </div>
    );
  }

  if (data.series.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
        Nenhum dado encontrado para esta visualização.
      </div>
    );
  }

  // Collect all group keys across all series items
  const allGroups = Array.from(new Set(data.series.flatMap((s) => Object.keys(s.groups))));
  const hasSecondary = !(allGroups.length === 1 && allGroups[0] === "(sem valor)");

  const titleBar = (
    <div className="flex items-center justify-between mb-4">
      <div className="text-sm font-medium text-muted-foreground">
        {view.name} · {data.total} total
      </div>
      {(onEdit || onDelete) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground">
              <Settings className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onEdit && <DropdownMenuItem onClick={onEdit}>Editar</DropdownMenuItem>}
            {onDelete && (
              <DropdownMenuItem className="text-destructive" onClick={onDelete}>
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );

  if (view.chartType === "pie") {
    // For pie: use primary dimension as segments, sum all groups
    const pieData = data.series.map((s) => ({
      name: s.dimension,
      value: Object.values(s.groups).reduce((a, b) => a + b, 0),
    }));

    return (
      <div className="p-6">
        {titleBar}
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              innerRadius={80}
              outerRadius={140}
              dataKey="value"
              label={({ name, percent }) => {
                const label = name.length > 16 ? `${name.slice(0, 14)}…` : name;
                return `${label} (${(percent * 100).toFixed(1)}%)`;
              }}
            >
              {pieData.map((entry, index) => (
                <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Bar chart — horizontal, stacked by secondary dimension
  // When no secondary grouping, rename "(sem valor)" to "Total" so tooltip is clean
  const displayGroups = hasSecondary
    ? allGroups
    : allGroups.map((g) => (g === "(sem valor)" ? "Total" : g));

  const chartData = data.series.map((item) => {
    const mapped: Record<string, string | number> = { dimension: item.dimension };
    for (const [key, val] of Object.entries(item.groups)) {
      mapped[key === "(sem valor)" && !hasSecondary ? "Total" : key] = val;
    }
    return mapped;
  });

  return (
    <div className="p-6">
      {titleBar}
      <ResponsiveContainer width="100%" height={Math.max(300, data.series.length * 32 + 80)}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 40, left: 120, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" allowDecimals={false} />
          <YAxis type="category" dataKey="dimension" width={110} tick={{ fontSize: 11 }} />
          <Tooltip
            formatter={(value: number, name: string) =>
              hasSecondary ? [value, name] : [value, "Candidatos"]
            }
          />
          {hasSecondary && <Legend />}
          {displayGroups.map((group, index) => (
            <Bar key={group} dataKey={group} stackId="a" fill={COLORS[index % COLORS.length]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
